import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Zap, ArrowLeft, X } from "lucide-react";
import { useInstantQuote } from "@/hooks/useInstantQuote";
import { useAuth } from "@/hooks/useAuth";
import { isRBRestoration } from "@/lib/orgConstants";
import { StepLocation } from "./instant-quote/StepLocation";
import { StepCustomer } from "./instant-quote/StepCustomer";
import { StepTrades } from "./instant-quote/StepTrades";
import { StepTemplate } from "./instant-quote/StepTemplate";
import { StepReady } from "./instant-quote/StepReady";
import { StepRbPrice, type RbPricingState, type RbQuoteResult } from "./instant-quote/StepRbPrice";
import { StepRbFinish } from "./instant-quote/StepRbFinish";
import { cn } from "@/lib/utils";

interface Props {
  trigger?: React.ReactNode;
}

export type QuoteWizardState = {
  address: string;
  lat?: number;
  lng?: number;
  name: string;
  phone: string;
  trades: string[];
  tier: "Good" | "Better" | "Best";
  templateId?: string;
};

const DEFAULT_STEPS = ["location", "customer", "trades", "template", "ready"] as const;
const RB_STEPS = ["location", "customer", "rb_price", "rb_finish"] as const;
type Step = (typeof DEFAULT_STEPS)[number] | (typeof RB_STEPS)[number];

export function DoorKnockerQuickQuote({ trigger }: Props) {
  const navigate = useNavigate();
  const { orgId } = useAuth();
  const isRb = isRBRestoration(orgId);
  const STEPS: readonly Step[] = isRb ? RB_STEPS : DEFAULT_STEPS;

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("location");
  const [state, setState] = useState<QuoteWizardState>({
    address: "",
    name: "",
    phone: "",
    trades: ["Roofing"],
    tier: "Better",
  });

  // RB-specific state
  const [pricing, setPricing] = useState<RbPricingState>({
    squares: null,
    pricePerSq: 450,
    material: "asphalt-30",
    wastePercent: 15,
    complexityPercent: 0,
  });
  const [rbQuote, setRbQuote] = useState<RbQuoteResult>({
    roofTotal: 0,
    tradesSubtotal: 0,
    commissionAmount: 0,
    grandTotal: 0,
    tradeCount: 0,
  });

  const { generateInstantQuote, isGenerating, progress, result } = useInstantQuote();

  const reset = () => {
    setStep("location");
    setState({ address: "", name: "", phone: "", trades: ["Roofing"], tier: "Better" });
    setPricing({
      squares: null,
      pricePerSq: 450,
      material: "asphalt-30",
      wastePercent: 15,
      complexityPercent: 0,
    });
    setRbQuote({ roofTotal: 0, tradesSubtotal: 0, commissionAmount: 0, grandTotal: 0, tradeCount: 0 });
  };

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("instant-quote-visibility", { detail: { open } }));
    return () => {
      window.dispatchEvent(new CustomEvent("instant-quote-visibility", { detail: { open: false } }));
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  const stepIndex = STEPS.indexOf(step as any);
  const goNext = () => setStep(STEPS[Math.min(stepIndex + 1, STEPS.length - 1)]);
  const goBack = () => setStep(STEPS[Math.max(stepIndex - 1, 0)]);

  const handleGenerate = async () => {
    const r = await generateInstantQuote(
      state.name.trim() || "New Lead",
      state.address.trim(),
      state.phone.trim() || undefined
    );
    if (r) goNext();
  };

  const handleViewProposal = () => {
    if (result?.proposal.shareId) {
      navigate(`/proposal/${result.proposal.shareId}`);
      close();
    }
  };

  const defaultTrigger = (
    <Button className="gap-2">
      <Zap className="h-4 w-4" /> Instant Quote
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <SheetTrigger asChild>{trigger || defaultTrigger}</SheetTrigger>
      <SheetContent
        side="bottom"
        hideCloseButton
        className="h-[92vh] sm:h-[88vh] sm:max-w-lg sm:right-0 sm:left-auto rounded-t-3xl sm:rounded-l-3xl sm:rounded-tr-none p-0 overflow-hidden flex flex-col border-border/40"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/40 bg-background/80 backdrop-blur-xl">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={stepIndex === 0 ? close : goBack}
          >
            {stepIndex === 0 ? <X className="h-4 w-4" /> : <ArrowLeft className="h-4 w-4" />}
          </Button>
          <div className="flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <div
                key={s}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === stepIndex ? "w-6 bg-primary" : i < stepIndex ? "w-1.5 bg-primary" : "w-1.5 bg-muted"
                )}
              />
            ))}
          </div>
          <div className="w-8" />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {step === "location" && (
            <StepLocation state={state} setState={setState} onNext={goNext} />
          )}
          {step === "customer" && (
            <StepCustomer state={state} setState={setState} onNext={goNext} />
          )}

          {/* RB Restoration flow */}
          {step === "rb_price" && (
            <StepRbPrice
              address={state.address}
              pricing={pricing}
              setPricing={(updater) => setPricing((p) => updater(p))}
              onNext={(quote) => {
                setRbQuote(quote);
                goNext();
              }}
            />
          )}
          {step === "rb_finish" && (
            <StepRbFinish
              customerName={state.name}
              customerPhone={state.phone}
              address={state.address}
              squares={pricing.squares ?? 0}
              pricePerSq={pricing.pricePerSq}
              wastePercent={pricing.wastePercent}
              complexityPercent={pricing.complexityPercent}
              roofTotal={rbQuote.roofTotal}
              tradesSubtotal={rbQuote.tradesSubtotal}
              commissionAmount={rbQuote.commissionAmount}
              finalPrice={rbQuote.grandTotal}
              tradeCount={rbQuote.tradeCount}
              onClose={close}
            />
          )}

          {/* Default flow */}
          {step === "trades" && (
            <StepTrades state={state} setState={setState} onNext={goNext} />
          )}
          {step === "template" && (
            <StepTemplate
              state={state}
              setState={setState}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              progress={progress}
            />
          )}
          {step === "ready" && result && (
            <StepReady result={result} onView={handleViewProposal} onNew={reset} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
